import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn'
import DateRangeIcon from '@mui/icons-material/DateRange'
import ErrorIcon from '@mui/icons-material/Error'
import LoyaltyIcon from '@mui/icons-material/Loyalty'
import LuggageIcon from '@mui/icons-material/Luggage'
import PersonIcon from '@mui/icons-material/Person'
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball'
import StickyNoteIcon from '@mui/icons-material/StickyNote2'
import { useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import { customColors } from '@themes/theme'

import { SxProps } from '@mui/material/styles'
import {
  BoardingIcon,
  CatIconEx,
  DaycareIcon,
  DogIconEx,
  GroomingIcon,
  PackageIcon,
  PetAddOnIcon,
  TemperTestIcon,
} from 'asset'

type TIconsSizes = 'small' | 'medium' | 'large'
interface TIconAvatarProps {
  icon: JSX.Element
  fillColor?: string
  backgroundColor?: string
  size?: TIconsSizes
  disabled?: boolean
  disabledFillColor?: string
  disabledBackgroundColor?: string
  sx?: SxProps
  reverseColors?: boolean
}
type TAvatarProps = Pick<TIconAvatarProps, 'fillColor' | 'backgroundColor' | 'size' | 'disabled' | 'sx'>
const iconSizes = {
  small: '24px',
  medium: '32px',
  large: '40px',
}

export const IconAvatar = ({
  icon,
  fillColor,
  backgroundColor,
  size = 'medium',
  disabled = false,
  disabledFillColor,
  disabledBackgroundColor,
  sx,
  reverseColors = false,
}: TIconAvatarProps) => {
  const theme = useTheme()
  fillColor = disabled ? disabledFillColor || theme.palette.action.disabled : fillColor || theme.palette.primary.main
  backgroundColor = disabled
    ? disabledBackgroundColor || theme.palette.action.disabledBackground
    : backgroundColor || theme.palette.primary.shade12
  const _size = iconSizes[size] ?? iconSizes.large

  if (reverseColors) {
    backgroundColor = fillColor
    fillColor = theme.palette.getContrastText(theme.palette.primary.main)
  }

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        ...sx,
        backgroundSize: 'contain',
        backgroundColor,
        height: _size,
        width: _size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignContent: 'center',
        '& svg': {
          fill: fillColor,
        },
      }}
    >
      {icon}
    </Stack>
  )
}

export const PersonIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<PersonIcon />} />
export const BoardingIconAvatar = (props: TAvatarProps) => (
  <IconAvatar {...props} icon={<BoardingIcon viewBox="1 1 19 19" />} />
)
export const CalendarIconAvatar = () => <IconAvatar icon={<DateRangeIcon />} />
export const PerksIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<AddCircleIcon />} />
export const BaseballIconAvatar = () => (
  <IconAvatar icon={<SportsBaseballIcon sx={{ transform: 'rotate(-20deg)' }} />} />
)
export const EmptyIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<svg />} />
export const LuggageIconAvatar = () => <IconAvatar icon={<LuggageIcon />} />
export const StickyNoteIconAvatar = () => <IconAvatar icon={<StickyNoteIcon />} />
export const AssignmentAvatarIcon = () => <IconAvatar icon={<AssignmentTurnedInIcon />} />
export const PetAddOnIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<PetAddOnIcon />} />
export const InvalidDrawerIcon = () => {
  const theme = useTheme()
  return (
    <IconAvatar
      icon={<ErrorIcon />}
      fillColor={theme.palette.error.main}
      backgroundColor={theme.palette.error.shade8}
    />
  )
}
export const AccountIconAvatar = (props: TAvatarProps) => <IconAvatar {...props} icon={<AccountCircleIcon />} />

export const BoardingAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      {...props}
      icon={<BoardingIcon viewBox="-2 -2 24 24" />}
      fillColor={theme.palette.primary.dark}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const PackageAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      {...props}
      icon={<PackageIcon />}
      fillColor={theme.palette.primary.dark}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const DaycareAvatar = (props: TAvatarProps) => {
  const { fillColor } = props
  return <IconAvatar {...props} icon={<DaycareIcon fill={fillColor} />} />
}

export const DaycareAvatarGeese = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      {...props}
      icon={<DaycareIcon fill={theme.palette.warning.main} />}
      fillColor={theme.palette.warning.main}
      backgroundColor={customColors.lightSecondary}
    />
  )
}

export const TemperTestAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      icon={<TemperTestIcon />}
      fillColor={theme.palette.success.main}
      backgroundColor={theme.palette.success.shade8}
      {...props}
    />
  )
}

export const GroomingAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      {...props}
      icon={<GroomingIcon viewBox="-2 -3 24 24" />}
      fillColor={theme.palette.primary.main}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

export const LoyaltyIconAvatar = (props: TAvatarProps) => {
  const theme = useTheme()
  return (
    <IconAvatar
      {...props}
      icon={<LoyaltyIcon viewBox="-2 -3 24 24" />}
      fillColor={theme.palette.primary.main}
      backgroundColor={theme.palette.primary.shade12}
    />
  )
}

interface TServiceTypeAvatar extends TAvatarProps {
  serviceType?: string
  color?: string
  fill?: string
  width?: number
  height?: number
}

export const ServiceTypeAvatar = (props: TServiceTypeAvatar) => {
  const theme = useTheme()
  const { serviceType } = props
  switch (serviceType) {
    case 'boarding':
      return <BoardingAvatar {...props} />
    case 'daycare':
      return <DaycareAvatar {...props} fillColor={theme.palette.primary.main} />
    case 'temper-test':
      return <TemperTestAvatar {...props} />
    case 'packages':
      return <PackageAvatar {...props} />
    case 'grooming':
      return <GroomingAvatar {...props} />
    case 'memberships':
      return <LoyaltyIconAvatar {...props} />
    default:
      return <EmptyIconAvatar {...props} />
  }
}

export const ServiceTypeIcon = (props: TServiceTypeAvatar) => {
  const theme = useTheme()
  const { serviceType, color, fill, ...restProps } = props
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mainColor = theme.palette[color || 'primary']?.main || theme.palette.primary.main
  switch (serviceType) {
    case 'boarding':
      return <BoardingIcon {...restProps} viewBox="-2 -2 24 24" />
    case 'daycare':
      return <DaycareIcon {...restProps} fill={fill || mainColor} />
    case 'temper-test':
      return <TemperTestIcon {...restProps} />
    case 'packages':
      return <PackageIcon {...restProps} />
    case 'grooming':
      return <GroomingIcon {...restProps} viewBox="-4 -3 24 24" />
    default:
      return <EmptyIconAvatar {...restProps} />
  }
}

interface TSpecieAvatarProps extends TAvatarProps {
  specie: string
  reverseColors: boolean
}

export const SpecieAvatar = (props: TSpecieAvatarProps) => {
  const { specie, size, reverseColors, disabled, ...rest } = props
  switch (specie) {
    case 'dog':
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<DogIconEx {...rest} fontSize={size} />}
        />
      )
    case 'cat':
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<CatIconEx {...rest} fontSize={size} />}
        />
      )
    default:
      return (
        <IconAvatar
          size={size}
          reverseColors={reverseColors}
          disabled={disabled}
          icon={<QuestionMarkIcon {...rest} fontSize={size} />}
        />
      )
  }
}
